import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Design System
import { Message } from 'jl-design-system/components/message/Message';

// Types
import { AppDispatch } from 'types/RootState.types';
import expressPaymentsState from './ExpressPayments.state';
import { ExpressPaymentsMethodProps } from './ExpressPayments.types';

// Config
import deliveryConstants from '@constants/deliveryConstants';
import { useWindowSize } from '@context/window/WindowSizeContext';
import { shouldShowExpressPaymentMethods } from '@redux/actions/payment/expressPaymentActions';

// Components
import PayPalButton from '@components/add-payment-details/paypal-button/PayPalButton';
import PayPalInvalidAddressModal from '@components/add-payment-details/paypal-invalid-address-modal';
import ApplePayExpress from '@components/apple-pay-express/ApplePayExpress';
import BodyText from '@components/body-text';
import Container from '@components/container';
import Heading from '@components/heading';

// Styles
import styles from './express-payments.scss';

const ExpressPayments = () => {
  const dispatch: AppDispatch = useDispatch();
  const {
    basketTotalRaw,
    deliveries,
    getPaymentPageComplete,
    getPayPalClientTokenCallActive,
    isApplePayExpressEnabled,
    isPayPalPayExpressEnabled,
    invalidPayPalBillingAddress,
    invalidPayPalShippingAddress,
    payPalServiceUnavailable,
    showExpressPayments,
    showExpressPaymentsFailedMessage,
    showAmendQuantityNotification,
    submitOrderFailed,
    applePayCompatibilityChecksComplete,
    payPalPayExpressCompatibilityChecksComplete,
    expressCountryCodeError,
  } = useSelector(expressPaymentsState);
  const { isDesktop } = useWindowSize();

  useEffect(() => {
    dispatch(shouldShowExpressPaymentMethods());
  }, [
    deliveries?.length,
    getPaymentPageComplete,
    getPayPalClientTokenCallActive,
    isApplePayExpressEnabled,
    isPayPalPayExpressEnabled,
    payPalServiceUnavailable,
    applePayCompatibilityChecksComplete,
    payPalPayExpressCompatibilityChecksComplete,
  ]);

  if (!showExpressPayments) return null;

  let deliveryChargeMessage;
  let deliveryMessage;

  const deliveryType = deliveries?.[0]?.type ?? '';

  if (deliveryType === deliveryConstants.GREEN_VAN_FLEET) {
    deliveryMessage =
      "The earliest standard or express delivery slots are available with express checkout. If you'd like to choose your own delivery slot, please continue with our regular checkout.";
  } else if (deliveryType === deliveryConstants.ONE_MAN) {
    deliveryChargeMessage = Number(basketTotalRaw) >= 50 ? 'Free standard delivery' : 'Standard delivery at £4.50';
    deliveryMessage = `${deliveryChargeMessage} is available with express checkout. Faster delivery options may be available. If you'd like to use collection, please continue with our regular checkout.`;
  } else {
    deliveryMessage =
      'Delivery from our approved supplier is available with express checkout. Delivery charges may apply.';
  }

  const renderMessage = () => {
    let messageProps;

    if (showAmendQuantityNotification && submitOrderFailed) {
      messageProps = {
        body: 'Sorry, we could not complete your order. This may be because your basket or selected delivery options have changed. Please review your order and try again.',
        title: 'Your order has not been placed',
        type: 'information',
      };
    } else if (showExpressPaymentsFailedMessage) {
      messageProps = {
        body: 'Please try again or select a delivery option below to place your order.',
        title: expressCountryCodeError
          ? 'Sorry, we cannot deliver to your selected country'
          : 'Sorry, we were unable to process this payment',
        type: 'error',
      };
    }

    if (messageProps) {
      return (
        <Container marginBottom="3">
          <Message {...messageProps} />
        </Container>
      );
    }

    return null;
  };

  const expressPaymentMethods: ExpressPaymentsMethodProps[] = [
    {
      key: 'applePayExpress',
      component: <ApplePayExpress />,
      condition: isApplePayExpressEnabled,
    },
    {
      key: 'paypalExpress',
      component: <PayPalButton type="paypalExpress" />,
      condition: isPayPalPayExpressEnabled,
    },
  ];

  const renderButtons = () =>
    expressPaymentMethods.map(({ component, condition, key }: ExpressPaymentsMethodProps) => {
      return (
        <Container key={key} renderIf={condition} width="full">
          {component}
        </Container>
      );
    });

  return (
    <>
      <Container background="white" className={styles.expressPayments} marginBottom="3" paddingX="3" paddingY="2">
        <Heading label="Express checkout" marginBottom="1" tag="h2" type="s" />
        {renderMessage()}
        <Container flex={{ flexDirection: isDesktop ? 'row' : 'col', gap: '2' }} marginBottom="2">
          {renderButtons()}
        </Container>
        <BodyText>{deliveryMessage}</BodyText>
      </Container>
      <p className={styles.or}>or</p>

      {isPayPalPayExpressEnabled && (
        <>
          {invalidPayPalBillingAddress && <PayPalInvalidAddressModal />}
          {invalidPayPalShippingAddress && <PayPalInvalidAddressModal addressType="shipping" />}
        </>
      )}
    </>
  );
};

export default ExpressPayments;
